import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdminSubscription,
  AdminSubscriptionUser,
  SubscriptionCatalog,
  assignAdminSubscription,
  createAdminSubscription,
  fetchAdminSubscriptionCatalog,
  fetchAdminSubscriptionUsers,
  fetchAdminSubscriptions,
  formatAccessSummary,
  formatAdminSubscriptionUserLabel,
  revokeAdminSubscriptionAssignment,
} from '../../../services/adminSubscriptionService';
import {
  AdminBreadcrumbsTw,
  AdminEmpty,
  AdminPageHeader,
  AdminTableWrap,
  btnPrimary,
  btnSecondary,
  filtersRowClass,
  inputClass,
  tdClass,
  textareaClass,
} from './adminUi';
import './AdminSubscriptionsSection.css';

type SubTab = 'users' | 'create' | 'manage';

function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function MultiSelectBlock({
  title,
  items,
  selected,
  onToggle,
  renderLabel,
}: {
  title: string;
  items: Array<{ id: string; label: string; meta?: string }>;
  selected: string[];
  onToggle: (id: string) => void;
  renderLabel?: (item: { id: string; label: string; meta?: string }) => React.ReactNode;
}) {
  return (
    <div className="admin-subscriptions-block">
      <div className="admin-subscriptions-block-title">{title}</div>
      {items.length === 0 ? (
        <div className="admin-subscriptions-muted">Нет данных</div>
      ) : (
        <div className="admin-subscriptions-checklist">
          {items.map((item) => (
            <label key={item.id} className="admin-subscriptions-check-item">
              <input
                type="checkbox"
                checked={selected.includes(item.id)}
                onChange={() => onToggle(item.id)}
              />
              <span>
                {renderLabel ? renderLabel(item) : item.label}
                {item.meta ? <span className="admin-subscriptions-muted"> · {item.meta}</span> : null}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminSubscriptionsSection() {
  const [subTab, setSubTab] = useState<SubTab>('users');
  const [catalog, setCatalog] = useState<SubscriptionCatalog | null>(null);
  const [users, setUsers] = useState<AdminSubscriptionUser[]>([]);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiMode, setAiMode] = useState<'unlimited' | 'limited'>('unlimited');
  const [aiLimit, setAiLimit] = useState('50');

  const [manageUserId, setManageUserId] = useState('');
  const [manageSubscriptionId, setManageSubscriptionId] = useState('');

  const loadCatalog = useCallback(async () => {
    const data = await fetchAdminSubscriptionCatalog();
    setCatalog(data);
  }, []);

  const loadUsers = useCallback(async () => {
    const res = await fetchAdminSubscriptionUsers({
      search: search.trim() || undefined,
      page: usersPage,
      limit: 25,
    });
    setUsers(res.data);
    setUsersTotal(res.total);
  }, [search, usersPage]);

  const loadSubscriptions = useCallback(async () => {
    const data = await fetchAdminSubscriptions();
    setSubscriptions(data);
  }, []);

  const reload = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      await Promise.all([loadCatalog(), loadUsers(), loadSubscriptions()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить данные подписок');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadCatalog, loadSubscriptions, loadUsers]);

  useEffect(() => {
    reload();
  }, [reload]);

  const visibleSectors = useMemo(() => {
    if (!catalog) return [];
    if (selectedIndustries.length === 0) return catalog.sectors;
    const industrySet = new Set(selectedIndustries);
    return catalog.sectors.filter((sector) => industrySet.has(String(sector.industryId)));
  }, [catalog, selectedIndustries]);

  const visibleCompanies = useMemo(() => {
    if (!catalog) return [];
    if (selectedSectors.length > 0) {
      const sectorSet = new Set(selectedSectors);
      return catalog.companies.filter((company) => sectorSet.has(String(company.sectorId)));
    }
    if (selectedIndustries.length > 0) {
      const industrySet = new Set(selectedIndustries);
      return catalog.companies.filter((company) => industrySet.has(String(company.industryId)));
    }
    return catalog.companies;
  }, [catalog, selectedIndustries, selectedSectors]);

  const canCreateSubscription = useMemo(() => {
    const hasDataScope =
      selectedIndustries.length > 0 ||
      selectedSectors.length > 0 ||
      selectedCompanies.length > 0 ||
      selectedGrades.length > 0;
    return createName.trim().length > 0 && (hasDataScope || aiEnabled);
  }, [aiEnabled, createName, selectedCompanies.length, selectedGrades.length, selectedIndustries.length, selectedSectors.length]);

  const onCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateSubscription) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createAdminSubscription({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        dataScope: {
          industries: selectedIndustries,
          sectors: selectedSectors,
          companies: selectedCompanies,
          gradeLevels: selectedGrades,
        },
        aiAccess: {
          enabled: aiEnabled,
          mode: aiMode,
          limit: aiMode === 'limited' ? Number(aiLimit) : undefined,
        },
      });
      setSuccess(`Подписка «${created.name}» создана`);
      setCreateName('');
      setCreateDescription('');
      setSelectedIndustries([]);
      setSelectedSectors([]);
      setSelectedCompanies([]);
      setSelectedGrades([]);
      setAiEnabled(false);
      setAiMode('unlimited');
      setAiLimit('50');
      await loadSubscriptions();
      setSubTab('manage');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось создать подписку');
    } finally {
      setSaving(false);
    }
  };

  const onAssignSubscription = async () => {
    const subscriptionId = Number(manageSubscriptionId);
    const userId = manageUserId.trim();
    if (!subscriptionId || !userId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await assignAdminSubscription(subscriptionId, userId);
      setSuccess('Подписка назначена пользователю');
      await Promise.all([loadUsers(), loadSubscriptions()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось назначить подписку');
    } finally {
      setSaving(false);
    }
  };

  const onRevokeAssignment = async (assignmentId: number) => {
    if (!window.confirm('Отозвать подписку у пользователя?')) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await revokeAdminSubscriptionAssignment(assignmentId);
      setSuccess('Подписка отозвана');
      await Promise.all([loadUsers(), loadSubscriptions()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отозвать подписку');
    } finally {
      setSaving(false);
    }
  };

  const manageAssignmentRows = useMemo(
    () => users.flatMap((user) => user.assignments.map((assignment) => ({ user, assignment }))),
    [users],
  );

  if (loading) {
    return <AdminEmpty>Загрузка подписок…</AdminEmpty>;
  }

  return (
    <>
      <AdminBreadcrumbsTw items={[{ label: 'Админ-панель' }, { label: 'Подписки' }]} />
      <AdminPageHeader
        title="Подписки"
        subtitle="Управление доступом пользователей к рыночным данным и AI-ассистенту"
        actions={(
          <button type="button" className={btnSecondary} onClick={reload} disabled={refreshing}>
            {refreshing ? 'Обновление…' : 'Обновить'}
          </button>
        )}
      />

      <div className="admin-subscriptions-tabs" role="tablist" aria-label="Раздел подписок">
        {([
          ['users', 'Пользователи'],
          ['create', 'Создать подписку'],
          ['manage', 'Управление'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={subTab === id}
            className={`admin-subscriptions-tab${subTab === id ? ' admin-subscriptions-tab-active' : ''}`}
            onClick={() => setSubTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="admin-subscriptions-alert admin-subscriptions-alert-error">{error}</div>}
      {success && <div className="admin-subscriptions-alert admin-subscriptions-alert-success">{success}</div>}

      {subTab === 'users' && (
        <div role="tabpanel">
          <div className={filtersRowClass}>
            <input
              className={inputClass}
              placeholder="Поиск по email, имени, логину"
              value={search}
              onChange={(e) => {
                setUsersPage(1);
                setSearch(e.target.value);
              }}
            />
            <button type="button" className={btnSecondary} onClick={loadUsers}>Найти</button>
          </div>

          <AdminTableWrap>
            <table className="admin-subscriptions-table">
              <thead>
                <tr>
                  <th>Email / ФИО</th>
                  <th>Тип аккаунта</th>
                  <th>Роли</th>
                  <th>Подписки</th>
                  <th>Доступ к данным</th>
                  <th>AI</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={6}><AdminEmpty>Пользователи не найдены</AdminEmpty></td></tr>
                ) : users.map((user) => (
                  <tr key={user.id}>
                    <td className={tdClass}>
                      <div className="font-medium text-slate-900">{formatAdminSubscriptionUserLabel(user)}</div>
                      <div className="admin-subscriptions-muted">
                        {user.isActive ? 'Активен' : 'Неактивен'}
                        {user.lastLoginAt ? ` · вход: ${new Date(user.lastLoginAt).toLocaleDateString('ru-RU')}` : ''}
                      </div>
                    </td>
                    <td className={tdClass}>{user.accountType === 'administrator' ? 'Администратор' : 'Пользователь'}</td>
                    <td className={tdClass}>
                      {user.roleCodes.length ? user.roleCodes.join(', ') : '—'}
                    </td>
                    <td className={tdClass}>
                      {user.assignments.filter((item) => item.status === 'active').length
                        ? user.assignments
                          .filter((item) => item.status === 'active')
                          .map((item) => item.subscription.name)
                          .join(', ')
                        : '—'}
                    </td>
                    <td className={tdClass}>
                      {user.accessSummaries.length
                        ? user.accessSummaries.map((item) => (
                          <div key={item.subscriptionId}>{formatAccessSummary(item.summary)}</div>
                        ))
                        : user.isAdmin ? 'Полный доступ (admin)' : '—'}
                    </td>
                    <td className={tdClass}>
                      {user.accessSummaries.some((item) => item.summary.aiAccess)
                        ? user.accessSummaries
                          .filter((item) => item.summary.aiAccess)
                          .map((item) => formatAccessSummary(item.summary))
                          .join('; ')
                        : user.isAdmin ? 'Безлимит (admin)' : `Trial used: ${user.aiTrialUsed}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>

          <div className="admin-subscriptions-pagination">
            <span>Всего: {usersTotal}</span>
            <div className="flex gap-2">
              <button type="button" className={btnSecondary} disabled={usersPage <= 1} onClick={() => setUsersPage((p) => p - 1)}>Назад</button>
              <button type="button" className={btnSecondary} disabled={usersPage * 25 >= usersTotal} onClick={() => setUsersPage((p) => p + 1)}>Вперёд</button>
            </div>
          </div>
        </div>
      )}

      {subTab === 'create' && catalog && (
        <form role="tabpanel" className="admin-subscriptions-create" onSubmit={onCreateSubscription}>
          <div className="admin-subscriptions-create-head">
            <label>
              <span>Название подписки</span>
              <input className={inputClass} value={createName} onChange={(e) => setCreateName(e.target.value)} required />
            </label>
            <label>
              <span>Описание</span>
              <textarea className={textareaClass} rows={3} value={createDescription} onChange={(e) => setCreateDescription(e.target.value)} />
            </label>
          </div>

          <div className="admin-subscriptions-grid">
            <MultiSelectBlock
              title="Индустрии"
              items={catalog.industries.map((item) => ({ id: String(item.id), label: item.name }))}
              selected={selectedIndustries}
              onToggle={(id) => setSelectedIndustries((prev) => toggleValue(prev, id))}
            />
            <MultiSelectBlock
              title="Сектора"
              items={visibleSectors.map((item) => ({
                id: String(item.id),
                label: item.name,
                meta: item.industryName,
              }))}
              selected={selectedSectors}
              onToggle={(id) => setSelectedSectors((prev) => toggleValue(prev, id))}
            />
            <MultiSelectBlock
              title="Грейды"
              items={catalog.gradeLevels.map((grade) => ({ id: String(grade), label: `Grade ${grade}` }))}
              selected={selectedGrades}
              onToggle={(id) => setSelectedGrades((prev) => toggleValue(prev, id))}
            />
          </div>

          <MultiSelectBlock
            title="Компании"
            items={visibleCompanies.slice(0, 300).map((item) => ({
              id: item.id,
              label: item.name,
              meta: `${item.sectorName} · ${item.industryName}`,
            }))}
            selected={selectedCompanies}
            onToggle={(id) => setSelectedCompanies((prev) => toggleValue(prev, id))}
          />
          {visibleCompanies.length > 300 && (
            <div className="admin-subscriptions-muted">Показаны первые 300 компаний. Сузьте выбор по индустрии или сектору.</div>
          )}

          <div className="admin-subscriptions-ai-block">
            <label className="admin-subscriptions-check-item">
              <input type="checkbox" checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} />
              <span>Выдать доступ к AI-ассистенту</span>
            </label>
            {aiEnabled && (
              <div className="admin-subscriptions-ai-options">
                <label className="admin-subscriptions-check-item">
                  <input
                    type="radio"
                    name="ai-mode"
                    checked={aiMode === 'unlimited'}
                    onChange={() => setAiMode('unlimited')}
                  />
                  <span>Безлимитные запросы</span>
                </label>
                <label className="admin-subscriptions-check-item">
                  <input
                    type="radio"
                    name="ai-mode"
                    checked={aiMode === 'limited'}
                    onChange={() => setAiMode('limited')}
                  />
                  <span>Ограниченное количество запросов</span>
                </label>
                {aiMode === 'limited' && (
                  <input
                    className={inputClass}
                    type="number"
                    min={1}
                    value={aiLimit}
                    onChange={(e) => setAiLimit(e.target.value)}
                    placeholder="Количество запросов"
                  />
                )}
              </div>
            )}
          </div>

          <div className="admin-subscriptions-actions">
            <button type="submit" className={btnPrimary} disabled={!canCreateSubscription || saving}>
              {saving ? 'Создание…' : 'Создать подписку'}
            </button>
          </div>
        </form>
      )}

      {subTab === 'manage' && (
        <div role="tabpanel" className="admin-subscriptions-manage">
          <div className="admin-subscriptions-manage-assign">
            <h3>Назначить подписку</h3>
            <div className={filtersRowClass}>
              <select className={inputClass} value={manageUserId} onChange={(e) => setManageUserId(e.target.value)}>
                <option value="">Выберите пользователя</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{formatAdminSubscriptionUserLabel(user)}</option>
                ))}
              </select>
              <select className={inputClass} value={manageSubscriptionId} onChange={(e) => setManageSubscriptionId(e.target.value)}>
                <option value="">Выберите подписку</option>
                {subscriptions.map((subscription) => (
                  <option key={subscription.id} value={subscription.id}>
                    {subscription.name} ({subscription.status})
                  </option>
                ))}
              </select>
              <button type="button" className={btnPrimary} disabled={!manageUserId || !manageSubscriptionId || saving} onClick={onAssignSubscription}>
                Выдать доступ
              </button>
            </div>
          </div>

          <AdminTableWrap>
            <table className="admin-subscriptions-table">
              <thead>
                <tr>
                  <th>Подписка</th>
                  <th>Пользователь</th>
                  <th>Состав доступа</th>
                  <th>Статус</th>
                  <th>Действие</th>
                </tr>
              </thead>
              <tbody>
                {manageAssignmentRows.length === 0 ? (
                  <tr>
                    <td colSpan={5}><AdminEmpty>Назначений пока нет</AdminEmpty></td>
                  </tr>
                ) : manageAssignmentRows.map(({ user, assignment }) => (
                  <tr key={`${user.id}-${assignment.id}`}>
                    <td className={tdClass}>{assignment.subscription.name}</td>
                    <td className={tdClass}>{formatAdminSubscriptionUserLabel(user)}</td>
                    <td className={tdClass}>
                      {formatAccessSummary(
                        subscriptions.find((item) => item.id === assignment.subscription.id)?.accessSummary
                        ?? { industries: [], sectors: [], companies: [], gradeLevels: [], packages: [], aiAccess: null },
                      )}
                    </td>
                    <td className={tdClass}>{assignment.status === 'active' ? 'Активна' : 'Отозвана'}</td>
                    <td className={tdClass}>
                      {assignment.status === 'active' ? (
                        <button type="button" className={btnSecondary} disabled={saving} onClick={() => onRevokeAssignment(assignment.id)}>
                          Забрать
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        </div>
      )}
    </>
  );
}
